import { useEffect, useState } from "react";
import { Box, Breadcrumbs, Divider, Link, Typography } from "@mui/joy";
import { ChevronRight } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";

import { Alert, Button, TextField } from "../../shared";
import {
    changePasswordValidation,
    userProfileValidation,
    type ChangePasswordFormValues,
    type UserProfileFormValues,
} from "../../libs/form-validations";
import { useUser } from "../../hooks/query";
import { useApiResponseParser, useAuth, useNotification } from "../../hooks";

function Breadcrumb() {
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 0, ml: 1, mr: 2 }}
        >
            <Breadcrumbs separator={<ChevronRight size={18} />}>
                <Link
                    component={RouterLink}
                    to="/dashboard"
                    level="title-lg"
                    sx={{
                        fontWeight: 500,
                        textDecoration: "none",
                        color: "inherit",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    Home
                </Link>
                <Typography level="body-md" sx={{ fontWeight: 500 }}>
                    Settings
                </Typography>
            </Breadcrumbs>
        </Box>
    );
}

function ProfileSection() {
    const { setUser, user } = useAuth();
    const { showSuccess } = useNotification();
    const { parseErrorResponseData } = useApiResponseParser();
    const { useGetMe, useUpdateProfile } = useUser();
    const { data: profile, isLoading } = useGetMe();
    const updateProfileSvc = useUpdateProfile();
    const [error, setError] = useState<string | null>(null);

    const methods = useForm<UserProfileFormValues>({
        resolver: userProfileValidation,
        mode: "onChange",
        defaultValues: {
            FirstName: "",
            LastName: "",
            Email: "",
        },
    });

    useEffect(() => {
        if (!profile) return;
        methods.reset({
            FirstName: profile.FirstName || "",
            LastName: profile.LastName || "",
            Email: profile.Email || "",
        });
    }, [profile, methods]);

    const onSubmit = methods.handleSubmit(async (data) => {
        setError(null);
        try {
            const updated = await updateProfileSvc.mutateAsync({
                FirstName: data.FirstName,
                LastName: data.LastName,
            });
            setUser({
                ...(user || {}),
                ...(updated || {}),
                FirstName: data.FirstName,
                LastName: data.LastName,
                Email: profile?.Email || user?.Email,
            });
            showSuccess("Profile updated");
        } catch (e) {
            setError(parseErrorResponseData(e) || "Failed to update profile");
        }
    });

    return (
        <Box sx={{ mb: 4 }}>
            <Typography level="title-md" sx={{ mb: 0.5 }}>
                Profile
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary", mb: 2 }}>
                Update your name. Email is managed by your sign-in account.
            </Typography>

            {error && (
                <Box mb={2}>
                    <Alert errorMessage={error} />
                </Box>
            )}

            <FormProvider {...methods}>
                <form onSubmit={onSubmit}>
                    <Box display="flex" flexDirection="column" gap={1.5}>
                        <TextField
                            name="FirstName"
                            label="First Name"
                            helperText="Your first name"
                            placeholder="e.g., Jane"
                            disabled={isLoading || updateProfileSvc.isPending}
                        />
                        <TextField
                            name="LastName"
                            label="Last Name"
                            helperText="Your last name"
                            placeholder="e.g., Doe"
                            disabled={isLoading || updateProfileSvc.isPending}
                        />
                        <TextField
                            name="Email"
                            label="Email"
                            helperText="Managed by Cognito — contact an admin to change"
                            placeholder="e.g., jane@example.com"
                            disabled
                        />
                        <Box display="flex" gap={1.5} mt={0.5}>
                            <Button
                                type="submit"
                                variant="solid"
                                color="primary"
                                loading={updateProfileSvc.isPending}
                                disabled={
                                    isLoading ||
                                    !methods.formState.isValid ||
                                    !methods.formState.isDirty
                                }
                            >
                                Save profile
                            </Button>
                        </Box>
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
}

function PasswordSection() {
    const { showSuccess } = useNotification();
    const { parseErrorResponseData } = useApiResponseParser();
    const { useChangePassword } = useUser();
    const changePasswordSvc = useChangePassword();
    const [error, setError] = useState<string | null>(null);

    const methods = useForm<ChangePasswordFormValues>({
        resolver: changePasswordValidation,
        mode: "onChange",
        defaultValues: {
            CurrentPassword: "",
            NewPassword: "",
            ConfirmPassword: "",
        },
    });

    const onSubmit = methods.handleSubmit(async (data) => {
        setError(null);
        try {
            await changePasswordSvc.mutateAsync({
                CurrentPassword: data.CurrentPassword,
                NewPassword: data.NewPassword,
            });
            methods.reset();
            showSuccess("Password updated");
        } catch (e) {
            setError(parseErrorResponseData(e) || "Failed to change password");
        }
    });

    return (
        <Box>
            <Typography level="title-md" sx={{ mb: 0.5 }}>
                Password
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary", mb: 2 }}>
                Change the password you use to sign in.
            </Typography>

            {error && (
                <Box mb={2}>
                    <Alert errorMessage={error} />
                </Box>
            )}

            <FormProvider {...methods}>
                <form onSubmit={onSubmit}>
                    <Box display="flex" flexDirection="column" gap={1.5}>
                        <TextField
                            name="CurrentPassword"
                            label="Current Password"
                            type="password"
                            helperText="Your current sign-in password"
                            placeholder="••••••••"
                            disabled={changePasswordSvc.isPending}
                        />
                        <TextField
                            name="NewPassword"
                            label="New Password"
                            type="password"
                            helperText="At least 8 characters"
                            placeholder="••••••••"
                            disabled={changePasswordSvc.isPending}
                        />
                        <TextField
                            name="ConfirmPassword"
                            label="Confirm New Password"
                            type="password"
                            helperText="Re-enter the new password"
                            placeholder="••••••••"
                            disabled={changePasswordSvc.isPending}
                        />
                        <Box display="flex" gap={1.5} mt={0.5}>
                            <Button
                                type="submit"
                                variant="solid"
                                color="primary"
                                loading={changePasswordSvc.isPending}
                                disabled={!methods.formState.isValid}
                            >
                                Update password
                            </Button>
                        </Box>
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
}

export default function Settings() {
    return (
        <Box
            flex={1}
            display="flex"
            flexDirection="column"
            maxHeight="100%"
            sx={{ overflow: "hidden", minWidth: 0, minHeight: 0 }}
        >
            <Breadcrumb />
            <Divider sx={{ mt: 0, ml: 0 }} />

            <Box flex={1} sx={{ minWidth: 0, overflow: "auto" }}>
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1, maxWidth: 700 }}>
                    <Typography level="title-lg">Settings</Typography>
                    <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                        Manage your profile and sign-in password.
                    </Typography>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 700 }}>
                    <ProfileSection />
                    <Divider sx={{ mb: 3 }} />
                    <PasswordSection />
                </Box>
            </Box>
        </Box>
    );
}
